<template>
  <form class="form">
    <div class="columns is-multiline">
      <label
        v-show="showCopy"
        class="checkbox"
      >
        <input
          type="checkbox"
          @change="$emit('copyContact',contact.contactType)"
        >
        {{ $t('domains.contacts.copy') }}
      </label>
      <div class="column is-full">
        <custom-input
          v-model="contact.firstName"
          :label="$t('common.firstname')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.firstName)}`"
          :error-text="errorGenerator($v.contact.firstName)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.firstName)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.lastName"
          :label="$t('common.lastname')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.lastName)}`"
          :error-text="errorGenerator($v.contact.lastName)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.lastName)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.emailAddress"
          :label="$t('common.email')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.emailAddress)}`"
          :error-text="errorGenerator($v.contact.emailAddress)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.emailAddress)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.companyName"
          :label="$t('common.company')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.companyName)}`"
          :error-text="errorGenerator($v.contact.companyName)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.companyName)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.addressLine1"
          :label="$t('common.address')"
          :input-class="`input ${hasErrorClass($v.contact.addressLine1)}`"
          :error-text="errorGenerator($v.contact.addressLine1)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.addressLine1)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.addressLine2"
          :input-class="`input ${hasErrorClass($v.contact.addressLine2)}`"
          :error-text="errorGenerator($v.contact.addressLine2)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.addressLine2)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.countryCode"
          stop-clearfix
          :label-as-a-container="false"
          :label="$t('common.country')"
          :error-text="errorGenerator($v.contact.countryCode)"
          :input-class="`input ${hasErrorClass($v.contact.countryCode)}`"
        >
          <multiselect
            v-model="country"
            :options="countries"
            :multiple="false"
            :placeholder="$t('common.select_country')"
            :close-on-select="true"
            track-by="code"
            label="name"
            :allow-empty="false"
            :show-labels="false"
            :searchable="true"
            :disabled="disableForm"
          />
        </custom-input>
      </div>

      <div class="column is-full">
        <custom-input
          v-show="!isCountryUS && hasSelectedCountry"
          v-model="contact.province"
          :label="$t('common.province')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.province)}`"
          :error-text="errorGenerator($v.contact.province)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.province)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-show="isCountryUS && hasSelectedCountry"
          v-model="contact.state"
          stop-clearfix
          :label-as-a-container="false"
          :label="$t('common.state')"
          :error-text="errorGenerator($v.contact.state)"
          :input-class="`input ${hasErrorClass($v.contact.state)}`"
          :disabled="disableForm"
        >
          <multiselect
            v-model="state"
            :options="ustates"
            :multiple="false"
            :placeholder="$t('common.select_state')"
            :close-on-select="true"
            track-by="code"
            label="name"
            :allow-empty="false"
            :show-labels="false"
            :searchable="true"
            :disabled="disableForm"
          />
        </custom-input>
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.city"
          :label="$t('common.city')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.city)}`"
          :error-text="errorGenerator($v.contact.city)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.city)"
        />
      </div>

      <div class="column is-full">
        <custom-input
          v-model="contact.postalCode"
          :label="$t('common.postalcode')"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.contact.postalCode)}`"
          :error-text="errorGenerator($v.contact.postalCode)"
          :disabled="disableForm"
          @input="delayTouch($v.contact.postalCode)"
        />
      </div>

      <div class="column is-half-desktop is-full-tablet">
        <custom-input
          id="internationalCode"
          v-model.lazy="internationalCode.code"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.internationalCode)}`"
          :label-as-a-container="false"
          :label="$t('common.international_code')"
          :error-text="errorGenerator($v.internationalCode)"
          @input="delayTouch(internationalCode.code)"
        >
          <multiselect
            v-model="internationalCode"
            :options="phoneCodeList"
            :multiple="false"
            :placeholder="$t('common.select_country')"
            :close-on-select="true"
            track-by="code"
            label="name"
            :allow-empty="false"
            :show-labels="false"
            :searchable="true"
          />
        </custom-input>
      </div>

      <div class="column is-half-desktop is-full-tablet">
        <input-custom
          id="single-phone"
          key="singlePhone"
          v-model.lazy="singlePhone"
          stop-clearfix
          :input-class="`input ${hasErrorClass($v.singlePhone)}`"
          type="text"
          :label="$t('business_listing.stepper.phone_number')"
          :error-text="errorGenerator($v.singlePhone)"
          info-text=""
          :placeholder="$t('common.single_phone')"
          @input="delayTouch($v.singlePhone)"
        />
      </div>

      <custom-input
        v-show="false"
        v-model="contact.telephoneNumber"
        :label="$t('common.phone')"
        stop-clearfix
        :input-class="`input ${hasErrorClass($v.contact.telephoneNumber)}`"
        :error-text="errorGenerator($v.contact.telephoneNumber)"
        :disabled="disableForm"
        :placeholder="$t('common.phone_example')"
        :info-text="$t('common.phone_example')"
        @input="delayTouch($v.contact.telephoneNumber)"
      />
    </div>
  </form>
</template>

<script>
import { SECONDS_IN_MS } from '@/helpers'
import CustomInput from '@/components/Shared/CustomInput'
import Multiselect from 'vue-multiselect'
import formHelperMixin from '@/mixins/form-helper'
import { required, minLength, email } from 'vuelidate/lib/validators'
import extraConstants from '@/helpers/form-validators.js'
import InputCustom from '@/components/Shared/Form/InputCustom'
import { checkInternationalCode, validProvince, validState, validCountry, validPhoneGoogleLib, singlePhoneGoogleLib } from '@/helpers/custom-validators.js'

export default {

  name: 'AddressForm',
  components: {
    CustomInput,
    Multiselect,
    InputCustom
  },
  mixins: [formHelperMixin],
  props: {
    data: {
      type: Object,
      default () {
        return {
          firstName: '',
          lastName: '',
          title: '',
          addressLine1: '',
          addressLine2: '',
          emailAddress: '',
          companyName: '',
          telephoneNumber: '',
          faxNumber: '',
          postalCode: '',
          city: '',
          state: '',
          province: '',
          countryCode: '',
          contactType: '',
          companyPositionHeld: '',
          hasError: false,
          hasChanged: false,
          copied: false
        }
      }
    },
    disableForm: {
      type: Boolean,
      default: false
    },
    showCopy: {
      type: Boolean,
      default: false
    },
    parsePhone: {
      type: Boolean,
      default: false
    }

  },
  data () {
    return {
      contact: this.data,
      country: { name: '', code: '' },
      state: { name: '', code: '' },
      copiedStatus: false,
      countries: [],
      ustates: extraConstants.USSTATESLIST,
      phoneCodeList: [],
      internationalCode: { name: '', code: '' },
      singlePhone: ''
    }
  },

  validations () {
    return {
      contact: {
        firstName: {
          required,
          minLength: minLength(2)
        },
        lastName: {
          required,
          minLength: minLength(2)
        },
        companyName: {
          minLength: minLength(3)
        },
        addressLine1: {
          required,
          minLength: minLength(3)
        },
        addressLine2: {
          minLength: minLength(3)
        },
        city: {
          required,
          minLength: minLength(3)
        },
        province: {
          required: true,
          validProvince: validProvince(this.isCountryUS),
          minLength: minLength(3)
        },
        state: {
          required: true,
          validState: validState(this.isCountryUS),
          minLength: minLength(2)
        },
        countryCode: {
          required,
          validCountry
        },
        postalCode: {
          required,
          minLength: minLength(2)
        },
        telephoneNumber: {
          required,
          validPhone: validPhoneGoogleLib(this.internationalCode)
        },
        emailAddress: {
          required,
          email
        }
      },

      internationalCode: {
        validCode: checkInternationalCode
      },

      singlePhone: {
        singlePhone: singlePhoneGoogleLib(this.internationalCode)
      }
    }
  },

  computed: {
    isCountryUS () {
      if (this.country.code === 'US') {
        return true
      }
      return false
    },

    hasSelectedCountry () {
      if (this.country.code !== '') {
        return true
      }
      return false
    }
  },
  watch: {

    contact: {
      handler (obj) {
        this.validateAllFields()
        this.copiedStatus = this.contact.copied
        setTimeout(SECONDS_IN_MS(1))
        this.contact.hasError = this.$v.contact.$anyError
        this.contact.hasChanged = this.$v.contact.$anyDirty

        if (this.contact.telephoneNumber.length > 0) {
          const pos = this.contact.telephoneNumber.indexOf('.')
          const code = this.contact.telephoneNumber.substr(1, pos - 1)
          if (code.length > 0) {
            this.internationalCode = this.phoneCodeList.find(v => v.code === code)
          } else {
            this.internationalCode = { name: '', code: '' }
          }
          this.singlePhone = this.contact.telephoneNumber.substr(pos + 1)
        }
      },
      deep: true
    },

    country: {
      handler (obj) {
        this.contact.countryCode = this.country.code
      }
    },

    state: {
      handler (obj) {
        this.contact.state = this.state.code
      }
    },

    copiedStatus: {
      handler (obj) {
        if (this.copiedStatus === true) {
          if (this.contact.state && this.contact.countryCode === 'US') {
            this.state = this.ustates.find(v => v.code === this.contact.state)
          } else {
            this.state = { name: '', code: '' }
          }
          if (this.contact.countryCode) {
            this.country = this.countries.find(v => v.code === this.contact.countryCode)
          } else {
            this.country = { name: '', code: '' }
          }
        } else {
          this.state = { name: '', code: '' }
          this.country = { name: '', code: '' }
          this.resetFormValidations()
        }
      }
    },

    singlePhone: {
      handler (obj) {
       
        if (this.singlePhone.length > 0) {
           this.contact.phoneNumber = '+' + this.internationalCode.code + '.' + this.singlePhone
           this.contact.telephoneNumber = this.contact.phoneNumber
        } else {
          this.contact.telephoneNumber = ''
        }
        this.$v.contact.telephoneNumber.$touch()
        this.$v.internationalCode.$touch()
      }
    },

    internationalCode: {
      handler (obj) {
        if (this.singlePhone.length > 0) {
          this.contact.phoneNumber = '+' + this.internationalCode.code + '.' + this.singlePhone
          this.$v.contact.telephoneNumber.$touch()
          this.$v.singlePhone.$touch()
        }
      }
    },

    parsePhone: {
      handler (obj) {
        this.singlePhone = this.singlePhone.replace(/^0+/, '')
      }
    }
  },

  beforeMount () {
    this.translateCountries()
    this.parseCountryCodes()
  },

  mounted () {
    if (this.contact.countryCode) {
      this.country = this.countries.find(v => v.code === this.contact.countryCode)
    }
    if (this.contact.state) {
      this.state = this.ustates.find(v => v.code === this.contact.state)
    }
  
    if (this.contact.telephoneNumber.length > 0) {
      const pos = this.contact.telephoneNumber.indexOf('.')
      const code = this.contact.telephoneNumber.substr(1, pos - 1)
      if (code.length > 0) {
        this.internationalCode = this.phoneCodeList.find(v => v.code === code)
      } else {
        this.internationalCode = { name: '', code: '' }
      }
      this.singlePhone = this.contact.telephoneNumber.substr(pos + 1)
    }
   
    if (typeof (this.contact.copied) === 'undefined') {
      this.copiedStatus = false
      this.contact.copied = false
    }
    if (typeof (this.contact.hasError) === 'undefined') {
      this.contact.hasError = false
    }
    if (typeof (this.contact.hasChanged) === 'undefined') {
      this.contact.hasChanged = false
    }
  },

  methods: {
    resetFormValidations () {
      this.$v.contact.firstName.$reset()
      this.$v.contact.lastName.$reset()
      this.$v.contact.addressLine1.$reset()
      this.$v.contact.postalCode.$reset()
      this.$v.contact.state.$reset()
      this.$v.contact.city.$reset()
      this.$v.contact.countryCode.$reset()
      this.$v.contact.emailAddress.$reset()
      this.$v.contact.telephoneNumber.$reset()
      this.$v.contact.province.$reset()
      this.$v.contact.state.$reset()
      this.$v.contact.$reset()
      this.$v.internationalCode.$reset()
      this.$v.singlePhone.$reset()
    },

    validateAllFields () {
      this.resetFormValidations()
      this.$v.contact.firstName.$touch()
      this.$v.contact.lastName.$touch()
      this.$v.contact.addressLine1.$touch()
      this.$v.contact.postalCode.$touch()
      this.$v.contact.state.$touch()
      this.$v.contact.city.$touch()
      this.$v.contact.countryCode.$touch()
      this.$v.contact.emailAddress.$touch()
      this.$v.contact.telephoneNumber.$touch()
      this.$v.internationalCode.$touch()
      this.$v.singlePhone.$touch()

      if (this.isCountryUS === true) {
        this.$v.contact.state.$touch()
        this.$v.contact.province.$reset()
      } else {
        this.$v.contact.province.$touch()
        this.$v.contact.state.$reset()
      }
    },

    translateCountries () {
      this.countries = JSON.parse(JSON.stringify(extraConstants.COUNTRIESLIST))
      this.countries.forEach(country => {
        country.name = this.$t(country.name)
      })
    },

    parseCountryCodes () {
      this.phoneCodeList = JSON.parse(JSON.stringify(extraConstants.COUNTRYPHONECODES))
      this.phoneCodeList.forEach(country => {
        country.name = this.$t(country.name) + ' (+' + country.code + ')'
      })
    }
  }

}
</script>

<style lang="scss" scoped>
.form {
  width: 100%;
  padding: 0 10px;
  margin-top: auto;
  text-align: left;
  float: left;
}
</style>
